import {
  DataFeedRowData,
  DanelConfiguration,
} from '../../../types/generatedTypes';
import {
  GridCellEditCommitParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { sanitizeStringValue } from '../../../utils/numberFormatter';
import { getInstrumentTypeFromMultiColumnMatching } from './danelUtils';

/**
 * Processes a row update with standardized logic for:
 * - String sanitization
 * - Instrument type mapping
 * - Multi-column matching
 */
export const processRowDataUpdate = (
  newRow: DataFeedRowData,
  oldRow: DataFeedRowData,
  danelConfig: DanelConfiguration | null
): DataFeedRowData => {
  const processedRow = { ...newRow };

  // Sanitize all string values to remove whitespace and hidden characters from copy/paste
  Object.keys(processedRow).forEach(field => {
    if (field !== 'id' && typeof processedRow[field] === 'string') {
      processedRow[field] = sanitizeStringValue(processedRow[field]);
    }
  });

  // Check for instrument type using multi-column matching only
  if (danelConfig) {
    const multiColumnInstrumentType = getInstrumentTypeFromMultiColumnMatching(
      processedRow,
      danelConfig
    );

    if (multiColumnInstrumentType) {
      // Multi-column matching found a match
      processedRow.instrumentType = multiColumnInstrumentType;
    }
  }

  return processedRow;
};

/**
 * Handles immediate cell edit to sanitize pasted values
 */
export const processCellEditCommit = (
  params: GridCellEditCommitParams,
  rows: DataFeedRowData[],
  updateRow: (
    newRow: DataFeedRowData,
    oldRow: DataFeedRowData
  ) => Promise<DataFeedRowData>
) => {
  const { id, field, value } = params;

  // Sanitize the value if it's a string
  const sanitizedValue =
    typeof value === 'string' ? sanitizeStringValue(value) : value;

  // If the value was changed by sanitization, trigger a mini update
  if (sanitizedValue !== value) {
    // Find the current row and create updated version
    const currentRow = rows.find(row => row.id === id);
    if (currentRow) {
      const updatedRow = { ...currentRow, [field]: sanitizedValue };
      // Use the existing updateRow mechanism but don't await it
      updateRow(updatedRow, currentRow).catch(() => {});
    }
  }

  return sanitizedValue;
};

/**
 * Creates a grid selection model compatible with MUI DataGrid
 * Handles both numeric and string IDs for raw and processed views
 */
export const createGridSelectionModel = (
  selectedRows: Array<number | string>
) => {
  return {
    type: 'include' as const,
    ids: new Set(selectedRows),
  };
};

/**
 * Processes row selection model changes from MUI DataGrid
 * Uses the actual rows being displayed to handle both numeric and string IDs
 */
export const processRowSelectionChange = (
  model: GridRowSelectionModel,
  currentRows: Array<{ id: number | string }>,
  isUploadingToIllio: boolean
): Array<number | string> => {
  if (isUploadingToIllio) {
    return [];
  }

  // Handle the selection model object with ids Set
  if (model && typeof model === 'object' && 'ids' in model) {
    const ids = model.ids;
    const type = model.type;

    if (ids instanceof Set) {
      if (type === 'include') {
        // Normal selection - return IDs as-is (can be number or string)
        return Array.from(ids);
      } else if (type === 'exclude') {
        // Select all mode - calculate selected rows by excluding the ids from displayed rows only
        const allDisplayedRowIds = currentRows.map(row => row.id);
        const excludedIds = Array.from(ids);
        return allDisplayedRowIds.filter(id => !excludedIds.includes(id));
      } else {
        return [];
      }
    }
  }

  return [];
};
